/**
 * Chat → contact-form hand-off (spec §6). The summary is the user's own need in
 * prose, so it must not travel in a URL (query strings are logged, shared,
 * indexed) and the existing CONTACT_PREFILL_SERVICE_EVENT cannot cross a
 * navigation. localStorage, slug-matched, 10-minute TTL, cleared on read.
 * Client-safe, pure.
 *
 * localStorage, NOT sessionStorage (2026-08-23): every link in the chat now
 * opens in a new tab, and a `target=_blank` tab (noopener is the browser
 * default) starts with an EMPTY sessionStorage — the prefill written a
 * millisecond before the click never reached the form. localStorage is shared
 * across tabs; the TTL and clear-on-read already bound how long the summary
 * can sit there. The composer DRAFT below stays in sessionStorage on purpose —
 * that one is meant to be per-tab.
 */
export const CONTACT_PREFILL_KEY = "et_chat_contact_prefill";
export const CONTACT_PREFILL_TTL_MS = 10 * 60 * 1000;
export const DRAFT_KEY = "et_chat_draft";

export interface ContactPrefill { slug: string; summary: string; at: number }

export function writeContactPrefill(slug: string, summary: string): void {
  try { localStorage.setItem(CONTACT_PREFILL_KEY, JSON.stringify({ slug, summary: summary.slice(0, 500), at: Date.now() })); } catch { /* private mode */ }
}

/** Returns the summary for this slug if fresh, and clears the entry either way. */
export function takeContactPrefill(slug: string, now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(CONTACT_PREFILL_KEY);
    if (!raw) return null;
    localStorage.removeItem(CONTACT_PREFILL_KEY);
    const v = JSON.parse(raw) as Partial<ContactPrefill>;
    if (v.slug !== slug || typeof v.summary !== "string" || typeof v.at !== "number") return null;
    if (now - v.at > CONTACT_PREFILL_TTL_MS) return null;
    return v.summary;
  } catch { return null; }
}

export interface ChatDraft {
  text: string;
  /**
   * `true` = ASK it on arrival, don't just type it in.
   *
   * Every writer today sets it, because each one records a question the
   * visitor has ALREADY committed to and was interrupted on: the mini chat bar
   * (they pressed send, we navigated), the anon login gate and the mid-chat
   * 401 (they pressed send, we demanded auth). In all three, prefilling the
   * composer instead would make them press send a second time for a turn they
   * already asked for — and the gate's own copy promises the message is kept.
   *
   * Keep the flag rather than assuming it: a draft saved for a visitor who did
   * NOT press send (an unsent box, say) must not spend a quota unit on their
   * behalf, and that writer would pass `auto: false`.
   *
   * Records written before this field existed parse as `auto: false`, i.e.
   * prefill — the safe direction.
   */
  auto: boolean;
  /** The chat topic a homepage pill stands for (an intent value), sent with
   *  the text so code sets it instead of the model (2026-10-14). */
  topic?: string;
}

/** Draft preserved across the login redirect (anon typed, then signed in). */
export function writeDraft(text: string, opts?: { auto?: boolean; topic?: string }): void {
  try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ text: text.slice(0, 1000), at: Date.now(), auto: opts?.auto === true, ...(opts?.topic ? { topic: opts.topic.slice(0, 40) } : {}) })); } catch { /* ignore */ }
}
export function takeDraft(now = Date.now()): ChatDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(DRAFT_KEY);
    const v = JSON.parse(raw) as { text?: string; at?: number; auto?: unknown; topic?: unknown };
    if (typeof v.text !== "string" || typeof v.at !== "number" || now - v.at > 30 * 60 * 1000) return null;
    return { text: v.text, auto: v.auto === true, ...(typeof v.topic === "string" && v.topic ? { topic: v.topic.slice(0, 40) } : {}) };
  } catch { return null; }
}
